import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import notificationService from '@/services/notificationService';
import { useAuth } from '@/core/contexts/AuthContext';

/**
 * Notification Query Keys
 */
export const notificationKeys = {
  all: ['notifications'],
  list: (userId) => [...notificationKeys.all, 'list', userId],
  unreadCounts: (userId) => [...notificationKeys.all, 'unreadCounts', userId]
};

/**
 * Get dashboard analytics (FAST - notification-based)
 * Replaces expensive getDashboardData calls
 */
export const useDashboardAnalytics = () => {
  const { user, getUserInfoForApi } = useAuth();
  
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  return useQuery({
    queryKey: [
      'notifications', 
      'dashboard', 
      user?.user_id, 
      user?.userType || user?.user_type, 
      user?.firstName, 
      user?.lastName
    ],
    queryFn: () => {
      const userInfo = getUserInfoForApi();
      return notificationService.getDashboardAnalytics(
        userInfo.user_id,
        userInfo.user_type,
        userInfo.user_name
      );
    },
    enabled: !!user?.user_id,
    staleTime: 5 * 60 * 1000, // 5 minutes - much longer than before since WebSocket updates
    refetchInterval: false, // Disable polling - WebSocket handles updates
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

/**
 * Get paginated notifications
 * UPDATED: Uses centralized notification service
 */
export const useNotifications = (options = {}) => {
  const { user } = useAuth();
  const userId = user?.userId || user?.user_id;
  const userType = user?.userType || user?.user_type || 'investor';
  
  return useQuery({
    queryKey: [...notificationKeys.list(userId), userType, options],
    queryFn: () => notificationService.getNotifications(userId, userType, options),
    enabled: !!userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: false, // Disable polling - WebSocket handles updates
  });
};

/**
 * Get unread notification counts by type
 * UPDATED: Uses centralized notification service
 */
export const useUnreadCounts = () => {
  const { user } = useAuth();
  const userId = user?.userId || user?.user_id;
  const userType = user?.userType || user?.user_type || 'investor';
  
  return useQuery({
    queryKey: [...notificationKeys.unreadCounts(userId), userType],
    queryFn: () => notificationService.getUnreadCounts(userId, userType),
    enabled: !!userId,
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchInterval: false, // Disable polling - WebSocket handles updates
  });
};

/**
 * Mark notification as read
 */
export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.userId || user?.user_id;
  
  const onSuccess = useCallback(() => {
    // Update notification list cache
    queryClient.invalidateQueries({ queryKey: notificationKeys.list(userId) });
    
    // Update unread counts cache
    queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCounts(userId) });
    
    // Update dashboard analytics cache (invalidate all dashboard queries)
    queryClient.invalidateQueries({ queryKey: ['notifications', 'dashboard'] });
  }, [queryClient, userId]);

  const onError = useCallback((error) => {
    console.error('Error marking notification as read:', error);
  }, []);
  
  return useMutation({
    mutationFn: (notificationId) => 
      notificationService.markAsRead(userId, notificationId),
    onSuccess,
    onError
  });
};

/**
 * Mark all notifications as read
 * UPDATED: Uses centralized notification service
 */
export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.userId || user?.user_id;
  const userType = user?.userType || user?.user_type || 'investor';
  
  const onSuccess = useCallback(() => {
    // Invalidate all notification-related queries
    queryClient.invalidateQueries({ queryKey: notificationKeys.all });
  }, [queryClient]);

  const onError = useCallback((error) => {
    console.error('Error marking all notifications as read:', error);
  }, []);
  
  return useMutation({
    mutationFn: () => notificationService.markAllAsRead(userId, userType),
    onSuccess,
    onError
  });
};

/**
 * Helper hook to invalidate notification queries when WebSocket updates arrive
 */
export const useInvalidateNotifications = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.userId || user?.user_id;
  
  return useCallback((updateType, data) => {
    console.log(`🔄 WebSocket update: ${updateType}`, data);
    
    switch (updateType) {
      case 'new_notification':
        // Invalidate unread counts and notification lists
        queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCounts(userId) });
        queryClient.invalidateQueries({ queryKey: notificationKeys.list(userId) });
        
        // Skip dashboard invalidation for analytics events - they're handled by direct cache updates
        if (data?.event_type !== 'ADD_TO_WATCHLIST' && 
            data?.event_type !== 'REMOVE_FROM_WATCHLIST' && 
            data?.event_type !== 'analytics_update') {
          console.log('🔄 [useInvalidateNotifications] Invalidating dashboard for non-analytics event:', data?.event_type);
          queryClient.invalidateQueries({ queryKey: ['notifications', 'dashboard'] });
        } else {
          console.log('🔄 [useInvalidateNotifications] Skipping dashboard invalidation for analytics event:', data?.event_type);
        }
        break;
        
      case 'notification':
        // Handle individual notification events (same logic as new_notification)
        queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCounts(userId) });
        queryClient.invalidateQueries({ queryKey: notificationKeys.list(userId) });
        
        // Skip dashboard invalidation for analytics events - they're handled by direct cache updates
        if (data?.event_type !== 'ADD_TO_WATCHLIST' && 
            data?.event_type !== 'REMOVE_FROM_WATCHLIST' && 
            data?.event_type !== 'analytics_update') {
          console.log('🔄 [useInvalidateNotifications] Invalidating dashboard for non-analytics notification:', data?.event_type);
          queryClient.invalidateQueries({ queryKey: ['notifications', 'dashboard'] });
        } else {
          console.log('🔄 [useInvalidateNotifications] Skipping dashboard invalidation for analytics notification:', data?.event_type);
        }
        break;
        
      case 'notification_read':
      case 'all_notifications_read':
        // Invalidate all notification caches
        queryClient.invalidateQueries({ queryKey: notificationKeys.all });
        break;
        
      default:
        console.log('Unknown notification update type:', updateType);
    }
  }, [queryClient, userId]);
}; 